import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Button
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeclarationWizard from '../components/CustomsDeclaration/DeclarationWizard';
import LoadingSpinner from '../components/LoadingSpinner';
import api from '../shared/services/api';
import { useTranslation } from '../store/languageStore';
import AppHeader from '../shared/components/AppHeader';

const CustomsDeclarationPage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { t, tString } = useTranslation();
  const [declaration, setDeclaration] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadDeclaration(id);
    } else {
      createNewDeclaration();
    }
  }, [id]);

  const loadDeclaration = async (declId: string) => {
    try {
      setLoading(true);
      const response = await api.get(`/api/v1/customs/declarations/${declId}`);
      setDeclaration(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load declaration');
    } finally {
      setLoading(false);
    }
  };

  const createNewDeclaration = async () => {
    try {
      setLoading(true);
      const response = await api.post('/api/v1/customs/declarations', {
        declaration_type: 'export',
        country_code: 'DE'
      });
      const newDecl = response.data;
      navigate(`/customs-declaration/${newDecl.id}`, { replace: true });

      // Load full declaration
      const fullResponse = await api.get(`/api/v1/customs/declarations/${newDecl.id}`);
      setDeclaration(fullResponse.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create declaration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data: any) => {
    if (!id) return;

    try {
      await api.put(`/api/v1/customs/declarations/${id}`, data);
      setDeclaration({ ...declaration, ...data });
    } catch (err: any) {
      console.error('Failed to save declaration:', err);
    }
  };

  const handleSubmit = async () => {
    if (!id) return;

    try {
      const response = await api.post(`/api/v1/customs/declarations/${id}/submit`);
      if (response.data.status === 'submitted') {
        navigate('/process');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to submit declaration');
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box>
      <AppHeader />
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {declaration && (
          <DeclarationWizard
            declaration={declaration}
            onSave={handleSave}
            onSubmit={handleSubmit}
          />
        )}
      </Container>
    </Box>
  );
};

export default CustomsDeclarationPage;
